import { addDays, addMonths, monthOf, weekStartOf } from '@wiztime/core';
import { dayPath, monthPath, weekPath } from '../app/routes';
import { ViewKind, type RouteView } from '../hooks/useRouteView';
import { GlobalShortcut } from './shortcuts';
import { viewDate } from './viewDate';

const DAYS_PER_WEEK = 7;

/** Where a global shortcut goes (2026-10b §10.2). */
export interface ShortcutNavigation {
  readonly path: string;
  /** Month view only: the date the grid focuses once shown (T focuses today). */
  readonly focusMonthDate?: string;
}

/**
 * The navigation a global shortcut causes on the current view, or null when it does nothing:
 * the view is not day / week / month / statistics, the shortcut already is the current view, or
 * `[` `]` on the statistics page (no period to step). The date follows the current view; the
 * week view hands over the day the day view last showed (else today inside the week, else its
 * first day), the month view its focused day (then that day again, else today in its month,
 * else the 1st).
 */
export function shortcutNavigation(
  shortcut: GlobalShortcut,
  view: RouteView,
  today: string,
  monthFocusedDate: string | null,
  lastDayDate: string | null,
): ShortcutNavigation | null {
  const kind = view.kind;
  if (kind !== ViewKind.Day && kind !== ViewKind.Week && kind !== ViewKind.Month && kind !== ViewKind.Stats) {
    return null;
  }
  const date = viewDate(view, today, monthFocusedDate, lastDayDate);
  const step = shortcut === GlobalShortcut.PreviousPeriod ? -1 : 1;
  switch (shortcut) {
    case GlobalShortcut.PreviousPeriod:
    case GlobalShortcut.NextPeriod:
      if (kind === ViewKind.Day) return { path: dayPath(addDays(date, step)) };
      if (kind === ViewKind.Week) {
        return { path: weekPath(weekStartOf(addDays(date, step * DAYS_PER_WEEK))) };
      }
      if (kind === ViewKind.Month) return { path: monthPath(addMonths(monthOf(date), step)) };
      return null;
    case GlobalShortcut.Today:
      if (kind === ViewKind.Week) return { path: weekPath(weekStartOf(today)) };
      if (kind === ViewKind.Month) return { path: monthPath(monthOf(today)), focusMonthDate: today };
      return { path: dayPath(today) };
    case GlobalShortcut.DayView:
      return kind === ViewKind.Day ? null : { path: dayPath(kind === ViewKind.Stats ? today : date) };
    case GlobalShortcut.WeekView:
      return kind === ViewKind.Week ? null : { path: weekPath(weekStartOf(date)) };
    case GlobalShortcut.MonthView:
      return kind === ViewKind.Month ? null : { path: monthPath(monthOf(date)) };
    default:
      return null;
  }
}
